import { create } from 'zustand';

/**
 * What the viewer is stepping through, and where in it.
 *
 * The two sources are two different sequences, and which one you are in depends on where you came
 * from. Opened from a card, the sequence is **the feed** — the next photograph is the next
 * advertiser, because that is what the reader was scrolling and one more picture of the same
 * person is not what they asked for. Opened from a gallery, the sequence is **that advertiser's
 * photographs**, because that is what they were looking at.
 *
 * `index` is a position in whichever of those two the source names, so a feed index is a listing
 * and a gallery index is a photograph.
 */
export interface ViewerTarget {
  source: 'feed' | 'gallery';
  /** The advertiser whose gallery is open. Unused by the feed, which walks the loaded listings. */
  username: string;
  index: number;
  /** The card's own image, shown as the first frame while the gallery loads. */
  poster?: string | null;
}

/**
 * How a page of results is drawn. `venues` is a city page's map of venues; a page that cannot draw
 * one (`/browse`) draws Cards for it and keeps the preference for the next city page.
 */
export type Layout = 'cards' | 'gallery' | 'venues';

const LAYOUT_KEY = 'ee.layout';
const LAYOUTS: readonly Layout[] = ['cards', 'gallery', 'venues'];

/** Remembered per browser, and nothing breaks without it: storage may be blocked or empty. */
export function readLayout(): Layout {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY);
    return LAYOUTS.find((layout) => layout === stored) ?? 'cards';
  } catch {
    return 'cards';
  }
}

interface UiState {
  /** How a page of results is drawn. A preference, not a filter: the same cards either way. */
  layout: Layout;
  setLayout: (layout: Layout) => void;
  /** The find sheet is owned by the shell but opened from several places, so its state lives here. */
  findOpen: boolean;
  setFindOpen: (open: boolean) => void;
  /**
   * The photo viewer, mounted once by the shell and opened from a card or a profile alike.
   * Null is closed, which is the same nullable-derived-open idiom the boost confirmation uses.
   */
  viewer: ViewerTarget | null;
  openViewer: (target: ViewerTarget) => void;
  closeViewer: () => void;
  setViewerIndex: (index: number) => void;
}

export const useUi = create<UiState>((set) => ({
  layout: readLayout(),
  setLayout: (layout) => {
    try {
      localStorage.setItem(LAYOUT_KEY, layout);
    } catch {
      // The choice still holds for this visit.
    }
    set({ layout });
  },
  findOpen: false,
  setFindOpen: (findOpen) => set({ findOpen }),
  viewer: null,
  openViewer: (viewer) => set({ viewer }),
  closeViewer: () => set({ viewer: null }),
  setViewerIndex: (index) =>
    set((state) => (state.viewer ? { viewer: { ...state.viewer, index } } : state)),
}));
